import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";
import { Theme } from "@material-ui/core/styles";
import { styled } from "@material-ui/styles";
import { useHistory } from "react-router-dom";

import ConfirmationModal from "../../../modules/ConfirmationModal";
import api from "../../../../api";
import { CAMPAIGN_STEPS , CampaignTemplate } from "../../../../types/campaignTypes";

import { ROUTE_PATHS } from "../../../../types/RoutePaths";
import { SecondaryButton } from "../../elements/Button";
import { basicSteps, stepOptions } from "../../../../constants/steps";

type CampaignTemplateCardProps = {
  template: CampaignTemplate;
  fetchTemplates: () => Promise<void>;
};

const Card = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: 220,
  background: theme.palette.brand.white,
  border: `1px solid ${theme.palette.brand.gray200}`,
  borderRadius: 10,
  overflow: "hidden",
  transition: "border-color 0.15s ease",
  "&:hover": {
    borderColor: theme.palette.brand.gray300,
  },
}));

const CardBody = styled("div")({
  padding: "18px 20px",
  flex: 1,
});

const CardTitle = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 15,
  fontWeight: 600,
  color: theme.palette.brand.gray900,
  marginBottom: 12,
}));

const StepsList = styled("ul")({
  listStyle: "none",
  margin: 0,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const StepItem = styled("li")({
  display: "flex",
  alignItems: "flex-start",
  gap: 8,
});

const StepIcon = styled("span")(({ theme }: { theme: Theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  marginTop: 1,
  color: theme.palette.brand.green,
}));

const StepLabel = styled(Typography)(({ theme }: { theme: Theme }) => ({
  fontSize: 13,
  color: theme.palette.brand.gray500,
  lineHeight: 1.4,
}));

const CardFooter = styled("div")(({ theme }: { theme: Theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  gap: 8,
  padding: "12px 16px",
  borderTop: `1px solid ${theme.palette.brand.gray100}`,
  background: theme.palette.brand.gray50,
}));

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const CampaignTemplateCard: FC<CampaignTemplateCardProps> = ({ template, fetchTemplates }) => {
  const history = useHistory();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  const customSteps = template.templateSteps.filter((step) => !basicSteps.includes(step));
  const getStepLabel = (step: CAMPAIGN_STEPS) => stepOptions.find((o) => o.name === step)?.label ?? step;

  const handleEdit = () => {
    history.push(ROUTE_PATHS.CAMPAIGN_TEMPLATE_EDIT.replace(":id", template.id), { template });
  };

  const handleConfirmDelete = async () => {
    setOpenConfirmationModal(false);
    try {
      await api.deleteCampaignTemplate(template.id);
      await fetchTemplates();
    } catch (error) {
      console.error("Failed to delete template:", error);
    }
  };

  return (
    <>
      <Card>
        <CardBody>
          <CardTitle>{template.templateName}</CardTitle>
          <StepsList>
            {customSteps.length > 0 ? (
              customSteps.map((step) => (
                <StepItem key={step}>
                  <StepIcon>
                    <CheckIcon />
                  </StepIcon>
                  <StepLabel>{getStepLabel(step)}</StepLabel>
                </StepItem>
              ))
            ) : (
              <StepLabel>No custom steps configured</StepLabel>
            )}
          </StepsList>
        </CardBody>
        <CardFooter>
          <SecondaryButton type="button" onClick={handleEdit}>
            <EditIcon />
            Edit
          </SecondaryButton>
          <SecondaryButton type="button" onClick={() => setOpenConfirmationModal(true)}>
            <DeleteIcon />
            Delete
          </SecondaryButton>
        </CardFooter>
      </Card>

      {openConfirmationModal && (
        <ConfirmationModal
          isOpen={openConfirmationModal}
          confirmTitle="Confirm Deletion"
          cancelTitle="No"
          isCancelButton
          confirmQuestion="Are you sure you want to delete this template?"
          confirmButtonLabel="Yes"
          handleClose={() => setOpenConfirmationModal(false)}
          handleConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
};

export default CampaignTemplateCard;
